// theme.ts - light/dark theme state. Pure helpers + tiny DOM/localStorage glue.
// The palette itself lives in styles/globals.css as CSS custom properties;
// switching themes only flips `data-theme` on <html>.

export type Theme = "dark" | "light";

export const THEME_STORAGE_KEY = "jw-pds-theme";
export const DEFAULT_THEME: Theme = "dark";

export function isTheme(v: unknown): v is Theme {
  return v === "dark" || v === "light";
}

// Stored preference wins; otherwise follow the OS. Unknown stored values
// (corrupted/legacy) fall back to the OS preference, not an error.
export function resolveInitialTheme(stored: string | null, prefersDark: boolean): Theme {
  if (isTheme(stored)) return stored;
  return prefersDark ? "dark" : "light";
}

export function nextTheme(theme: Theme): Theme {
  return theme === "dark" ? "light" : "dark";
}

// ---- DOM / storage glue (no React dependency) ----

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  // tailwind.config.ts 가 darkMode:"class" 이므로 dark: 유틸리티는 클래스를 본다.
  // 색 토큰 자체는 data-theme 으로 뒤집히니 dark: 는 예외 상황용 탈출구지만,
  // 둘이 어긋나면 조용히 틀린다 - 항상 같이 세팅한다.
  document.documentElement.classList.toggle("dark", theme === "dark");
}

export function getAppliedTheme(): Theme {
  const t = document.documentElement.dataset.theme;
  return isTheme(t) ? t : DEFAULT_THEME;
}

// localStorage can throw (Safari private mode, blocked storage) - never let
// theme persistence crash the app.
export function readStoredTheme(): string | null {
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function persistTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // best-effort only
  }
}

export function initTheme(): void {
  const prefersDark =
    typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
      : true;
  applyTheme(resolveInitialTheme(readStoredTheme(), prefersDark));
}
